/**
 * MyToolLauncher — launches a purchased tool inside the buyer dashboard
 * context. Ownership-gated: verifies the buyer's email owns the product
 * (via /api/my/purchases) before rendering the interactive tool.
 *
 * Routed as: /my-tool/:productId?email=&sessionId=
 *
 * Product routing:
 *   cost_calculator   → CostCalculatorPage (skipGate)
 *   deal_analyzer     → DealAnalyzerPage (skipGate)
 *   negotiation_coach → NegotiationCoachPage (skipGate)
 *   rv_comparison     → RVComparePage
 *   buyer_toolkit     → ToolkitHub (grid of the 4 included tools)
 *   vip_buying_plan   → VIPHub (VipBookingCard + toolkit grid)
 *   vip_concierge     → VIPHub (VipBookingCard + toolkit grid)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Lock, Calculator, Search, Gavel, Scale, Package, Crown, ChevronRight, Bell } from 'lucide-react';
import CostCalculatorPage from './CostCalculatorPage';
import DealAnalyzerPage from './DealAnalyzerPage';
import NegotiationCoachPage from './NegotiationCoachPage';
import RVComparePage from './RVComparePage';
import VipBookingCard from '../components/dashboard/VipBookingCard';
import UnifiedDashboardNudge from '../components/dashboard/UnifiedDashboardNudge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Products that expand to include a set of tools (kept in sync with backend).
const BUNDLE_INCLUDES = {
  buyer_toolkit:   ['cost_calculator', 'deal_analyzer', 'negotiation_coach', 'rv_comparison', 'daily_matches'],
  vip_buying_plan: ['cost_calculator', 'deal_analyzer', 'negotiation_coach', 'rv_comparison', 'daily_matches', 'buyer_toolkit'],
  vip_concierge:   ['cost_calculator', 'deal_analyzer', 'negotiation_coach', 'rv_comparison', 'daily_matches', 'buyer_toolkit', 'vip_buying_plan'],
};

const TOOL_META = {
  cost_calculator:   { label: 'Cost Calculator',   sub: 'True 5-year ownership cost',    icon: Calculator, color: '#22c55e' },
  deal_analyzer:     { label: 'Deal Analyzer',     sub: 'Steal, fair, or rip-off?',      icon: Search,     color: '#ec4899' },
  negotiation_coach: { label: 'Negotiation Coach', sub: 'Scripts + counter-offer math',  icon: Gavel,      color: '#6366f1' },
  rv_comparison:     { label: 'RV Comparison',     sub: 'Side-by-side AI-rated',         icon: Scale,      color: '#0ea5e9' },
  daily_matches:     { label: 'Daily Matches',     sub: '3 fresh AI picks every day',    icon: Bell,       color: '#f59e0b' },
};

const MyToolLauncher = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const email = (searchParams.get('email') || '').trim().toLowerCase();
  const sessionId = searchParams.get('sessionId') || null;
  const backTo = sessionId ? `/my-matches/${sessionId}` : '/my-matches';

  const [ownedIds, setOwnedIds] = useState(null); // null = loading, Set once loaded

  useEffect(() => {
    if (!email || !email.includes('@')) { setOwnedIds(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/my/purchases`, { params: { email } });
        if (!cancelled) {
          setOwnedIds(new Set((res.data?.products || []).map(p => p.product_id).filter(Boolean)));
        }
      } catch {
        if (!cancelled) setOwnedIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  if (ownedIds === null) {
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center" data-testid="tool-launcher-loading">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
      </div>
    );
  }

  const owns = ownedIds.has(productId);

  if (!owns) {
    return <NotOwned productId={productId} backTo={backTo} />;
  }

  // RV Comparison: use its own page (already accepts sessionId + has its own back button).
  if (productId === 'rv_comparison') {
    return <RVComparePage />;
  }

  // Direct interactive tools.
  const backLabel = 'My Matches';
  if (productId === 'cost_calculator')   return <CostCalculatorPage   skipGate sessionId={sessionId} backTo={backTo} backLabel={backLabel} />;
  if (productId === 'deal_analyzer')     return <DealAnalyzerPage     skipGate sessionId={sessionId} backTo={backTo} backLabel={backLabel} />;
  if (productId === 'negotiation_coach') return <NegotiationCoachPage sessionId={sessionId} backTo={backTo} backLabel={backLabel} />;

  // Bundle / VIP: render a hub of the included tools.
  if (productId === 'buyer_toolkit') {
    return <ToolkitHub email={email} sessionId={sessionId} backTo={backTo} ownedIds={ownedIds} product="buyer_toolkit" />;
  }
  if (productId === 'vip_buying_plan' || productId === 'vip_concierge') {
    return <VIPHub email={email} sessionId={sessionId} backTo={backTo} ownedIds={ownedIds} product={productId} />;
  }

  // Unknown product for the launcher — bounce home.
  return <Navigate to={backTo} replace />;
};

const NotOwned = ({ productId, backTo }) => (
  <div className="min-h-screen bg-[#05070d] text-white" data-testid="tool-launcher-not-owned">
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <div className="w-14 h-14 rounded-2xl bg-amber-500/15 text-amber-300 inline-flex items-center justify-center mb-4">
        <Lock className="w-6 h-6" />
      </div>
      <h1 className="text-2xl font-black text-white mb-2">You don&apos;t own this tool yet</h1>
      <p className="text-sm text-slate-400 mb-6">
        Grab it from the store and it&apos;ll unlock here automatically after checkout.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link
          to={`/checkout/${productId}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#05070d] text-sm font-black"
          data-testid="tool-launcher-get-it"
        >
          Get this tool
        </Link>
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-slate-200 text-sm font-bold hover:bg-white/[0.05]"
          data-testid="tool-launcher-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
      </div>
    </div>
  </div>
);

const HubShell = ({ title, subtitle, backTo, accent, children, testid }) => (
  <div className="min-h-screen bg-[#05070d] text-white" data-testid={testid}>
    <div className="border-b border-white/10 sticky top-0 z-30 bg-[#05070d]/85 backdrop-blur-md">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="hub-back">
          <ArrowLeft className="w-4 h-4" /> My Matches
        </Link>
      </div>
    </div>
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-0">
      <div className={`absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full ${accent === 'vip' ? 'bg-fuchsia-500' : 'bg-amber-400'} opacity-10 blur-[120px]`} />
      <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-10 blur-[120px]" />
    </div>
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">{title}</h1>
        <p className="text-sm text-slate-400 max-w-2xl">{subtitle}</p>
      </div>
      {children}
    </div>
  </div>
);

const ToolGrid = ({ email, sessionId, ownedIds, toolIds }) => {
  const items = useMemo(() => toolIds.map(id => ({ id, meta: TOOL_META[id] })).filter(t => t.meta), [toolIds]);
  const qs = new URLSearchParams({ ...(email ? { email } : {}), ...(sessionId ? { sessionId } : {}) }).toString();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="hub-tool-grid">
      {items.map(({ id, meta }) => {
        const Icon = meta.icon;
        const unlocked = ownedIds.has(id);
        const href = unlocked ? `/my-tool/${id}?${qs}` : `/checkout/${id}`;
        return (
          <Link
            key={id}
            to={href}
            className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 p-4 transition-colors"
            data-testid={`hub-tool-${id}`}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}22`, color: meta.color }}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-white leading-tight">{meta.label}</p>
              <p className="text-xs text-slate-400 truncate">{meta.sub}</p>
            </div>
            <span className={`shrink-0 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${unlocked ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30' : 'bg-white/[0.06] text-slate-400 border border-white/10'}`}>
              {unlocked ? 'Open' : 'Get it'}
            </span>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white" />
          </Link>
        );
      })}
    </div>
  );
};

const ToolkitHub = ({ email, sessionId, backTo, ownedIds }) => (
  <HubShell
    title="Buyer Toolkit"
    subtitle="Your complete buyer&apos;s arsenal — every interactive tool is unlocked and lives right here. Open any one to run it on your own numbers."
    backTo={backTo}
    testid="toolkit-hub"
  >
    {/* Nudge tool-only buyers without a quiz completion. Silent when a sessionId exists. */}
    <div className="mb-6">
      <UnifiedDashboardNudge variant="quiz" email={email} sessionId={sessionId} />
    </div>
    <div className="mb-4 flex items-center gap-2">
      <Package className="w-4 h-4 text-amber-300" />
      <span className="text-xs font-black uppercase tracking-widest text-amber-300">Included tools</span>
    </div>
    <ToolGrid
      email={email}
      sessionId={sessionId}
      ownedIds={ownedIds}
      toolIds={BUNDLE_INCLUDES.buyer_toolkit}
    />
  </HubShell>
);

const VIPHub = ({ email, sessionId, backTo, ownedIds, product }) => {
  const name = product === 'vip_concierge' ? 'VIP Concierge' : 'VIP Buying Plan';
  const subtitle = product === 'vip_concierge'
    ? 'Your dedicated advisor is on-call. Book any of your 3 included 30-min consultations here — every tool below is unlocked while your case is open.'
    : 'Your personal RV Match advisor is standing by. Book your included 30-min consultation here — every tool below is unlocked while your case is open.';
  return (
    <HubShell
      title={name}
      subtitle={subtitle}
      backTo={backTo}
      accent="vip"
      testid={`vip-hub-${product}`}
    >
      {/* Quiz nudge for tool-only VIP buyers who haven't taken the match quiz. */}
      <div className="mb-6">
        <UnifiedDashboardNudge variant="quiz" email={email} sessionId={sessionId} />
      </div>
      <div className="mb-8">
        <div className="mb-3 flex items-center gap-2">
          <Crown className="w-4 h-4 text-fuchsia-300" />
          <span className="text-xs font-black uppercase tracking-widest text-fuchsia-300">Book your VIP call</span>
        </div>
        <VipBookingCard email={email} name="" />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Package className="w-4 h-4 text-cyan-300" />
        <span className="text-xs font-black uppercase tracking-widest text-cyan-300">Included tools</span>
      </div>
      <ToolGrid
        email={email}
        sessionId={sessionId}
        ownedIds={ownedIds}
        toolIds={['cost_calculator', 'deal_analyzer', 'negotiation_coach', 'rv_comparison']}
      />
    </HubShell>
  );
};

export default MyToolLauncher;
